// src/components/AnnouncementsPanel.js
import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  Header,
  SpaceBetween,
  StatusIndicator,
  Alert,
  Table,
  TextFilter,
  Pagination
} from '@awsui/components-react';
import { api } from '../../services/TrendsApi';

export function AnnouncementsPanel() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [announcements, setAnnouncements] = useState([]);
  const [filterText, setFilterText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showTable, setShowTable] = useState(false);

  // Fetch announcements from the API
  const fetchAnnouncements = async () => {
    setIsLoading(true);
    setError('');
    setShowTable(false);
    
    try {
      const data = await api.getAnnouncements();
      console.log('Announcements data:', data);
      
      // Extract announcements from the response
      let extractedAnnouncements = [];
      if (data && Array.isArray(data.announcements)) {
        extractedAnnouncements = data.announcements;
      } else if (data && data.body && Array.isArray(data.body.announcements)) {
        extractedAnnouncements = data.body.announcements;
      }
      
      // Add a small delay before setting the announcements
      setTimeout(() => {
        setAnnouncements(extractedAnnouncements);
        setShowTable(true);
      }, 300);
    } catch (err) {
      console.error('Error fetching announcements:', err);
      setError(`Error fetching announcements: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };
  // Memoize filtered data
  const filteredData = React.useMemo(() => {
    if (!filterText || filterText.trim() === '') {
      return announcements;
    }
    
    const lowerFilterText = filterText.toLowerCase();
    return announcements.filter(item => 
      (item.Title && item.Title.toLowerCase().includes(lowerFilterText)) ||
      (item.Summary && item.Summary.toLowerCase().includes(lowerFilterText)) ||
      (item.Competitor && item.Competitor.toLowerCase().includes(lowerFilterText))
    );
  }, [announcements, filterText]);

  return (
    <Container>
      <SpaceBetween size="l">
        <Header variant="h2">Get Competitor Announcements</Header>
        
        <Box padding="l" variant="awsui-key-label">
          <Button
            variant="primary"
            onClick={fetchAnnouncements}
            loading={isLoading}
          >
            Fetch Announcements
          </Button>
        </Box>
        
        {error && (
          <Alert type="error" header="Error">
            {error}
          </Alert>
        )}
        
        {showTable && announcements.length > 0 && (
          <div>
            <Box variant="awsui-key-label">Announcements ({announcements.length})</Box>
            <Table
              columnDefinitions={[
                {
                  id: 'Title',
                  header: 'Title',
                  cell: item => item?.Title || 'N/A',
                  sortingField: 'Title'
                },
                {
                  id: 'Competitor',
                  header: 'Competitor',
                  cell: item => item?.Competitor || 'N/A',
                  sortingField: 'Competitor'
                },
                {
                  id: 'PublishDate',
                  header: 'Publish Date',
                  cell: item => item?.PublishDate || 'N/A',
                  sortingField: 'PublishDate'
                },
                {
                  id: 'Summary',
                  header: 'Summary',
                  cell: item => item?.Summary || 'N/A'
                }
              ]}
              items={filteredData}
              loading={isLoading}
              loadingText="Loading announcements"
              filter={
                <TextFilter
                  filteringPlaceholder="Find announcements"
                  filteringText={filterText}
                  onChange={({ detail }) => setFilterText(detail.filteringText)}
                />
              }
              pagination={
                <Pagination
                  currentPageIndex={currentPage}
                  onChange={({ detail }) => setCurrentPage(detail.currentPageIndex)}
                  pagesCount={Math.max(1, Math.ceil(filteredData.length / 10))}
                />
              }
              empty={
                <Box textAlign="center" padding="l">
                  <b>No announcements found</b>
                  <Box padding={{ bottom: 's' }}>
                    No announcements match the current filter criteria.
                  </Box>
                </Box>
              }
            />
          </div>
        )}
        
        {showTable && announcements.length === 0 && (
          <Alert type="info" header="No announcements">
            No announcements were found. Try fetching announcements again.
          </Alert>
        )}
      </SpaceBetween>
    </Container>
  );
}
